import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
// eslint-disable-next-line import/no-cycle
import { Card } from "../../Card";
import {
	CardTheme,
	CardAsType,
	CardShape,
	CardContext,
	CardOrientation,
	CardImageRatio,
} from "../CardType/CardType";
import { HeadingAsElement, HeadingWeight } from "../../../Heading";
// eslint-disable-next-line import/no-cycle
import { CardSlider } from "../CardSlider/CardSlider";
import * as styles from "./CardBuilder.module.scss";

const LogoPositions = {
	topLeft: "topLeft",
	topRight: "topRight",
	bottomLeft: "bottomLeft",
	bottomRight: "bottomRight",
	bottomCenter: "bottomCenter",
};

const CardBuilder = ({
	className,
	theme,
	shape,
	orientation,
	clickable,
	type,
	images,
	logo,
	logoPosition,
	link,
	renderImage,
	renderLink,
	head,
	headAs,
	headWeight,
	context,
	children,
	isSponsored,
	hotelTags,
	sliderNav,
	ratio,
	ribbon,
	tab,
}) => (
	<Card.Type
		theme={theme}
		shape={shape}
		type={type}
		orientation={orientation}
		context={context}
		className={cx(styles.card, {
			[className]: className,
		})}>
		{head && (
			<Card.Header size={headAs} weight={headWeight} className={styles.cardHead}>
				{head.length >= 50 ? `${head.substring(0, 50)}...` : head}
			</Card.Header>
		)}
		<div
			className={styles.cardImageContainer}
			data-gallery={images?.length > 1}
			data-shape={`image-${head ? "hotel" : shape || "none"}`}>
			{images?.length && (
				<CardSlider sliderNav={sliderNav} ratio={ratio} renderImage={renderImage} images={images} />
			)}
			{logo && (
				<Card.Logo
					logo={logo}
					logoPosition={logoPosition}
					context="logo"
					imageComponentProps={{ renderImage }}
				/>
			)}
			{tab && (
				<div className={styles.cardTab} style={{ "--_tab-color": `var(--color-${tab.theme})` }}>
					{tab.content}
				</div>
			)}
			{hotelTags.length > 0 && (
				<ul className={styles.cardHotelTags} data-cardel="tags">
					{hotelTags.map(item => (
						<li key={item.type} className={styles.cardHotelTagsItem}>
							{item.label}
						</li>
					))}
				</ul>
			)}
			{isSponsored && <span className={styles.cardSponsored}>Sponsored</span>}
			{!!ribbon && (
				<span className={styles.ribbon}>
					<span className={styles.ribbonContent}>#{ribbon}</span>
				</span>
			)}
		</div>
		<Card.Body className={styles.cardBody} data-context={`body-${context || "none"}`}>
			{children}
		</Card.Body>
		{clickable &&
			renderLink(link, {
				className: styles.cardLink,
				children: <span className="sr-only">{link.text}</span>,
			})}
	</Card.Type>
);

CardBuilder.propTypes = {
	className: PropTypes.string,
	/**
	 * Required image
	 */
	images: PropTypes.arrayOf(
		PropTypes.shape({
			fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			source: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
			alt: PropTypes.string,
			src: PropTypes.string,
		})
	),
	/**
	 * Renders optional logo on card image
	 */
	logo: PropTypes.shape({
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logoPosition: PropTypes.oneOf(Object.values(LogoPositions)),
	head: PropTypes.string,
	headAs: PropTypes.oneOf(Object.keys(HeadingAsElement)),
	headWeight: PropTypes.oneOf(Object.keys(HeadingWeight)),
	children: PropTypes.node.isRequired,
	isSponsored: PropTypes.bool,
	hotelTags: PropTypes.arrayOf(PropTypes.string),
	/**
	 * Renders if onClick is available
	 */
	button: PropTypes.shape({
		icon: PropTypes.string,
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		text: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
	}),
	/**
	 * Renders links to appear as button and will be applied to card
	 */
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		text: PropTypes.string,
	}),
	/**
	 * Renders heading color dynamically
	 */
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
	/**
	 * Structure props
	 */
	// FIXME: theme: PropTypes.oneOf(Object.keys(CardTheme)),
	theme: PropTypes.string,
	shape: PropTypes.oneOf(Object.keys(CardShape)),
	type: PropTypes.oneOf(Object.keys(CardAsType)),
	orientation: PropTypes.oneOf(Object.keys(CardOrientation)),
	clickable: PropTypes.bool,
	context: PropTypes.oneOf(Object.keys(CardContext)),
	sliderNav: PropTypes.bool,
	ratio: PropTypes.oneOf(Object.keys(CardImageRatio)),
	ribbon: PropTypes.string,
	tab: PropTypes.shape({
		theme: PropTypes.string,
		content: PropTypes.string,
	}),
};

CardBuilder.defaultProps = {
	className: "",
	logo: null,
	logoPosition: LogoPositions.bottomLeft,
	head: undefined,
	isSponsored: false,
	hotelTags: [],
	headAs: HeadingAsElement.h2,
	headWeight: HeadingWeight.normal,
	button: null,
	link: null,
	images: [],
	renderImage: () => {},
	renderLink: (link, { className, children }) => (
		<a href={link.to} className={className}>
			{children}
		</a>
	),
	theme: CardTheme.none,
	shape: CardShape.none,
	type: CardShape.shadow,
	orientation: CardOrientation.vertical,
	clickable: true,
	context: CardContext.none,
	sliderNav: false,
	ratio: undefined,
	ribbon: null,
	tab: null,
};

export default CardBuilder;
export { CardBuilder, LogoPositions };
